// workflowLimitsCheck — Pure helper som räknar entiteter i en WorkflowState
// (eller MultiSheetWorkflowState) och jämför mot PlanLimits. Använder
// `withinLimit` från planTiers.ts. Returnerar detaljerad rapport så UI
// kan visa exakt vilka gränser som överskrids ("Blocks: 50 > 20 limit").

import type { PlanLimits } from '@/lib/planTiers';
import type {
  WorkflowState,
  MultiSheetWorkflowState,
} from '@/types/workflow';
import { isMultiSheetState } from '@/types/workflow';

// Skiljer sig från planTiers.withinLimit: den funktionen används vid
// "kan vi skapa en till?" (strikt <). Vid import kollar vi "ryms state:n"
// vilket är <=. -1 = unlimited.
function exceeds(current: number, limit: number): boolean {
  return limit >= 0 && current > limit;
}

export interface LimitViolation {
  field: 'blocks' | 'connectors' | 'sheets' | 'groups' | 'layers';
  current: number;
  limit: number;
}

export interface LimitCheckReport {
  withinLimits: boolean;
  violations: LimitViolation[];
  counts: {
    blocks: number;
    connectors: number;
    sheets: number;
    groups: number;
    layers: number;
  };
}

/**
 * Räknar entity-counts och jämför mot limits.
 *
 * `maxBlocks` / `maxConnectors` / `maxTopLevelGroups` / `maxNestedLayers`
 * är PER SHEET (inte per workspace). En workspace får ha upp till
 * `maxSheets` sheets och varje sheet har sin egen budget. Vi rapporterar
 * därför maximum-per-sheet (det värsta arbetsbladet) — ryms det värsta
 * bladet ryms alla. `maxSheets` är det enda workspace-globala måttet här.
 * `-1` betyder unlimited i PlanLimits.
 */
export function checkImportLimits(
  state: WorkflowState | MultiSheetWorkflowState,
  limits: PlanLimits,
): LimitCheckReport {
  let blocks = 0;
  let connectors = 0;
  let groups = 0;
  let layers = 0;
  let sheets = 1;

  if (isMultiSheetState(state)) {
    sheets = state.sheets.length;
    for (const s of state.sheets) {
      let sheetGroups = 0;
      let sheetLayers = 0;
      for (const g of s.groups) {
        if (g.collapsed) sheetLayers++;
        else sheetGroups++;
      }
      // Per-sheet limits → jämför mot det värsta bladet, inte summan.
      blocks = Math.max(blocks, s.blocks.length);
      connectors = Math.max(connectors, s.connectors.length);
      groups = Math.max(groups, sheetGroups);
      layers = Math.max(layers, sheetLayers);
    }
  } else {
    blocks = state.blocks.length;
    connectors = state.connectors.length;
    for (const g of state.groups) {
      if (g.collapsed) layers++;
      else groups++;
    }
  }

  const violations: LimitViolation[] = [];
  if (exceeds(blocks, limits.maxBlocks)) {
    violations.push({ field: 'blocks', current: blocks, limit: limits.maxBlocks });
  }
  if (exceeds(connectors, limits.maxConnectors)) {
    violations.push({ field: 'connectors', current: connectors, limit: limits.maxConnectors });
  }
  if (exceeds(sheets, limits.maxSheets)) {
    violations.push({ field: 'sheets', current: sheets, limit: limits.maxSheets });
  }
  if (exceeds(groups, limits.maxTopLevelGroups)) {
    violations.push({ field: 'groups', current: groups, limit: limits.maxTopLevelGroups });
  }
  if (exceeds(layers, limits.maxNestedLayers)) {
    violations.push({ field: 'layers', current: layers, limit: limits.maxNestedLayers });
  }

  return {
    withinLimits: violations.length === 0,
    violations,
    counts: { blocks, connectors, sheets, groups, layers },
  };
}

/** Human-readable sammanfattning för toast/banner. */
export function describeViolations(violations: LimitViolation[]): string {
  return violations
    .map(v => `${labelForField(v.field)}: ${v.current} > ${v.limit}`)
    .join(', ');
}

function labelForField(field: LimitViolation['field']): string {
  switch (field) {
    case 'blocks': return 'Blocks';
    case 'connectors': return 'Connectors';
    case 'sheets': return 'Sheets';
    case 'groups': return 'Groups';
    case 'layers': return 'Layers';
  }
}
